import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { api, type Me } from '../api.ts';
import { track } from '../analytics.ts';
import { LANGUAGES, setLanguage } from '../i18n.ts';
import { ConsentBanner } from './ConsentBanner.tsx';

export function Layout({ me, children }: { me: Me | null; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [path, navigate] = useLocation();
  const qc = useQueryClient();
  const hoursLeft = me?.demo ? Math.max(1, Math.round((new Date(me.demo.expiresAt).getTime() - Date.now()) / 3600_000)) : 0;

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' });
    navigate(me?.demo ? '/demo' : '/login');
    // Drop everything of the old session, then refetch "me" (401 → logged-out screens).
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    await qc.resetQueries({ queryKey: ['me'] });
  }

  return (
    <>
      <a className="skip" href="#main">{t('nav.skip')}</a>
      {me?.demo && (
        <div className="demo-banner" role="region" aria-label={t('nav.demo')}>
          <div className="container row" style={{ justifyContent: 'space-between' }}>
            <p>{t('demo.bannerText', { hours: hoursLeft })}</p>
            <a className="btn btn--small" href={me.demo.quoteUrl} onClick={() => track('quote_cta_click', { placement: 'banner' })}>
              {t('demo.cta')} →
            </a>
          </div>
        </div>
      )}
      <header className="header">
        <div className="container row" style={{ justifyContent: 'space-between', minHeight: '3.75rem' }}>
          <Link href="/" className="logo">{t('brand')}</Link>
          <div className="row">
            {me && (
              <nav aria-label={t('nav.main')} className="row">
                {(['/', '/connections', '/settings'] as const).map((href, i) => (
                  <Link key={href} href={href} aria-current={(href === '/' ? path === '/' || path.startsWith('/devices') : path === href) ? 'page' : undefined} className="nav-link">
                    {t(['nav.devices', 'nav.connections', 'nav.settings'][i]!)}
                  </Link>
                ))}
                <span className="faint">{me.organization.name}</span>
                <button className="link-btn" onClick={logout}>{me.demo ? t('nav.exitDemo') : t('nav.logout')}</button>
              </nav>
            )}
            <div className="lang" role="group" aria-label={t('nav.language')}>
              {LANGUAGES.map((l) => (
                <button key={l} type="button" lang={l} aria-pressed={i18n.language === l} onClick={() => setLanguage(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>
      </header>
      <main id="main" className="container main">{children}</main>
      <ConsentBanner />
    </>
  );
}
